export default class AnchorScroll {

  constructor(id) {

    if (!id || !window.matchMedia( '(min-width: 1024px)' ).matches) {
      return;
    }

    this.scrollNavigation = document?.getElementById( id );
    if (!this.scrollNavigation) {
      return;
    }

    this.header = document?.getElementById( 'site-header' );
    this.offset = Math.ceil( (document.body.classList.contains( 'admin-bar' ) ? this.header.offsetHeight + 32 : this.header.offsetHeight) );
    this.activeClass = 'denhaag-anchor-navigation__link--active';
    this.current = null;
    this.scrollTimer = null;
    this.previousKey = false;
    this.focusElement = null;

    this.getScrollPosition();
    this.links = Array.from( this.scrollNavigation?.getElementsByTagName( 'A' ) );
    this.sections = this.getSections();
    const contentContainer = document.querySelector( '.page-container__columns' );

    this.keyboardFocusableElements = Array.from(
      contentContainer.querySelectorAll(
        'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
      )
    ).filter( el => !el.disabled && el.getAttribute( 'aria-hidden' ) !== 'true' );

    this.windowEvents();
    this.eventsLoop();
  }

  eventsLoop() {
    /**
     * Tabbing through sidebar links.
     */
    this.links?.forEach( (link) => {
      link?.addEventListener( 'click', (e) => {
        // Event preventDefault, so we can scroll with offset.
        e.preventDefault();
        this.scrollToSection( link.hash );
      } );

      link?.addEventListener( 'keydown', (e) => {
        switch (e.code || e.key) {
          case 'Enter':
          case 'Space':
            e.preventDefault();
            this.scrollToSection( link.hash );
            this.setFocusToSection();
            break;
          case 'Tab':
            // Tab
            this.focusElement = null; // Reset value.
            break;
        }
      } );
    } );

    /**
     * Tabbing through the page content.
     */
    [...this.keyboardFocusableElements]?.forEach( (el, index) => el.addEventListener( 'keyup', (e) => {
      if ('Tab' === (e.code || e.key)) {

        const collapse = el.closest( '.denhaag-anchor-collapse' );
        if (!!collapse) {
          this.current = collapse.getAttribute( 'id' );
          this.setActive();
        } else {
          // Loop through other elements.
          const fallbackElements = [...e.composedPath()].filter( (el) => el.classList && el.classList.contains( 'denhaag-anchor-collapse' ) );

          if (fallbackElements && fallbackElements.length > 0) {
            this.current = fallbackElements[fallbackElements.length - 1].getAttribute( 'id' );
            this.setActive();
          }
        }
      }
    } ) );
  }

  /**
   * Set focus to heading of the specific section.
   * This template al H2 are set as collapse-label.
   */
  setFocusToSection() {
    this.focusElement = document.getElementById( `denhaag-anchor-collapse-label-${this.current}` );
    document.getElementById( `denhaag-anchor-collapse-label-${this.current}` )?.focus();

    this.setActive();
  }

  /**
   * Get Current scrollPosition and update value.
   */
  getScrollPosition() {
    this.scrollPos = document.documentElement.scrollTop - (document.documentElement.clientTop || 0);
  }

  /**
   * Fire onClick events.
   * @param {string} input
   */
  scrollToSection(input) {
    let id = this.getId( input );

    if (!id) {
      console.warn( 'AnchorScroll->scrollToSection(): no valid ID on input.', typeof input, input );
      return;
    }

    this.current = id;

    const activeLinks = this.links?.filter( link => link.hash === input );
    activeLinks?.forEach( link => link.classList.add( this.activeClass ) );

    // Filter out the active links, far more efficient than looping through all links.
    let inActiveLinks = this.links.filter( l => !activeLinks.includes( l ) );
    inActiveLinks?.forEach( link => link.classList.remove( this.activeClass ) );

    const target = document?.getElementById( id );

    if (!target) {
      console.warn( 'AnchorScroll->scrollToSection(): no target element.' );
      return;
    }

    // Scroll to Element, with some offset. This way it isn't glued to the top of the screen.
    try {
      target.scrollIntoView( {
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      } );
    } catch (error) {
      //fallback to prevent browser crashing
      target.scrollIntoView( false );
    }
  }

  /**
   * Get sections within the content.
   *
   * @return {*[]|unknown[]|undefined}
   */
  getSections() {
    if (0 === this.links.length) {
      return [];
    }

    // Filter out links that point to a non-existent section id, otherwise onScroll() crashes on `section.offsetTop`.
    return this.links?.map( link => document?.getElementById( this.getId( link ) ) ).filter( Boolean );
  }

  /**
   * Update active on scroll position.
   */
  onScroll() {
    clearTimeout( this.scrollTimer )
    this.scrollTimer = setTimeout( this.userStoppedScrolling, 500 );

    setTimeout( () => {
      // Update active on scrollPosition.
      [...this.sections]?.forEach( section => {
        if (this.scrollPos >= (Math.ceil( section.offsetTop ) - this.offset)) {
          this.current = section.getAttribute( 'id' );
        }
      } );

      if (null === this.current) {
        // Remove hash from url when no section is active.
        this.updateUrl( '', '' );
      }

      this.setActive();
      // Timeout calculation is 25ms longer than probably needed, but added it due to long pages lings. Therefor we also used a calculation on the length of the amount of sections.
    }, Math.floor( [...this.sections].length * 100 ) );
  }

  userStoppedScrolling = () => {
    const active = document.querySelector( `.${this.activeClass}` );

    const sectionId = active?.attributes?.href?.value
    if (sectionId) {
      this.updateUrl( sectionId.slice( 1 ), active?.innerText );
    }
  }

  setActive() {
    // Set active based on Y position of section.
    this.links?.forEach( link => link.classList.toggle( this.activeClass, this.getId( link ) === this.current ) );
  }

  /**
   * Url (hash) to string.
   * @param {object|string} input
   * @return {string}
   */
  getId(input) {
    let id = typeof input === 'string' ? input : input.getAttribute( 'href' );
    return id.slice( id.indexOf( '#' ) + 1 );
  }

  /**
   * Update url with ID and hash it.
   *
   * @param {string} id The ID of the section.
   * @param {string} title The title of the section.
   */
  updateUrl(id, title) {
    if (!!id && (!!window.location.hash && window.location.hash.slice( 1 ) !== id || !window.location.hash)) {
      // Add or update hash to current url.
      window.history.replaceState( {}, `${title} - ${document.title}`, `#${id}` );
    } else if (!id) {
      // Remove hash from current url.
      window.history.replaceState( {}, document.title, `${window.location.origin}${window.location.pathname}` );
    }
  }

  /**
   * Events fired on window events.
   */
  windowEvents() {
    const self = this;
    let isScrolling;
    window.addEventListener( 'scroll', () => {
      clearTimeout( isScrolling );
      self.getScrollPosition();
      isScrolling = setTimeout( () => self.onScroll(), 100 );
    }, false );
    window.addEventListener( 'orientationchange', () => this.getScrollPosition(), false );
    window.addEventListener( 'hashchange', (e) => this.scrollToSection( window.location.hash ) );
  }
}
